import { useState } from 'react';
import { Logo } from '@wyocspay/ui';
import type { ReactNode } from 'react';

/**
 * Panel chrome - the "Ledger" design: a white, grouped icon sidebar and a light
 * content column with a slim top bar. The nav is data-driven (headings and
 * items with optional badges); an icon is chosen per item key.
 */
export interface NavItem {
  key: string;
  label: string;
  badge?: number;
}
export type NavEntry = { heading: string } | NavItem;

/** A 15px stroke icon in the shared frame. */
function icon(children: ReactNode): ReactNode {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

const NAV_ICONS: Record<string, ReactNode> = {
  overview: icon(<><path d="M2.4 6.5 8 2.2l5.6 4.3v6.3a.9.9 0 0 1-.9.9H3.3a.9.9 0 0 1-.9-.9z" /><path d="M6.3 13.7V9.2h3.4v4.5" /></>),
  payments: icon(<><rect x="1.7" y="3.4" width="12.6" height="9.2" rx="1.6" /><path d="M1.7 6.6h12.6" /></>),
  'new-payment': icon(<><circle cx="8" cy="8" r="5.9" /><path d="M8 5.4v5.2M5.4 8h5.2" /></>),
  invoices: icon(<><path d="M3.4 1.9h9.2v12.2l-1.8-1.1-1.8 1.1-1.8-1.1-1.8 1.1-1.8-1.1z" /><path d="M5.8 5.2h4.4M5.8 7.8h4.4M5.8 10.4h2.6" /></>),
  balances: icon(<><path d="M1.8 6.2 8 2.4l6.2 3.8" /><path d="M3.4 6.9v5.5M6.6 6.9v5.5M9.4 6.9v5.5M12.6 6.9v5.5M1.8 13.6h12.4" /></>),
  payouts: icon(<><path d="M8 13V3.4" /><path d="M4.4 6.9 8 3.2l3.6 3.7" /></>),
  'payment-methods': icon(<><rect x="1.7" y="4" width="12.6" height="8.6" rx="1.5" /><path d="M4.4 9.4h2.2" /></>),
  terminals: icon(<><rect x="4" y="1.8" width="8" height="12.4" rx="1.6" /><path d="M6.4 4.6h3.2M6.8 11.6h2.4" /></>),
  'stripe-payments': icon(<><path d="M2.4 8a5.6 5.6 0 0 1 9.6-3.9l1.6 1.5" /><path d="M13.6 2.6v3.2h-3.2" /><path d="M13.6 8a5.6 5.6 0 0 1-9.6 3.9L2.4 10.4" /><path d="M2.4 13.4v-3.2h3.2" /></>),
  disputes: icon(<><path d="M8 2.6 14 13H2z" /><path d="M8 6.6v3M8 11.4h.01" /></>),
  onboarding: icon(<><path d="M8 1.9 13.2 4v4c0 3.1-2.1 5.3-5.2 6.1C4.9 13.3 2.8 11.1 2.8 8V4z" /><path d="M5.9 7.9 7.4 9.4 10.3 6" /></>),
  verification: icon(<><path d="M8 1.9 13.2 4v4c0 3.1-2.1 5.3-5.2 6.1C4.9 13.3 2.8 11.1 2.8 8V4z" /><path d="M5.9 7.9 7.4 9.4 10.3 6" /></>),
  features: icon(<><path d="M2.6 5h10.8M2.6 11h10.8" /><circle cx="5.6" cy="5" r="1.7" /><circle cx="10.4" cy="11" r="1.7" /></>),
  packages: icon(<><path d="M8 2 3 4.5v7L8 14l5-2.5v-7z" /><path d="M3 4.5 8 7l5-2.5M8 7v7" /></>),
  'account-details': icon(<><circle cx="8" cy="5.6" r="2.6" /><path d="M3 13c0-2.6 2.2-4.2 5-4.2s5 1.6 5 4.2" /></>),
  settings: icon(<><rect x="2.6" y="2.6" width="10.8" height="10.8" rx="1.6" /><path d="M5.4 6h5.2M5.4 8.4h5.2M5.4 10.8h3" /></>),
  dac7: icon(<><circle cx="8" cy="8" r="6" /><path d="M2.2 8h11.6M8 2.1c1.7 1.9 2.5 4 2.5 5.9s-.8 4-2.5 5.9c-1.7-1.9-2.5-4-2.5-5.9s.8-4 2.5-5.9" /></>),
  'tax-registration': icon(<><path d="M4.4 2.4 6 4l2-1.6L10 4l1.6-1.6v11.2L10 12l-2 1.6L6 12l-1.6 1.6z" /><path d="M6.4 6.4h3.2M6.4 9h2.4" /></>),
  documents: icon(<><path d="M4 2h5l3 3v9H4z" /><path d="M9 2v3h3M6 8.2h4M6 10.6h4" /></>),
  developers: icon(<><path d="M6 5 3 8l3 3M10 5l3 3-3 3" /></>),
  'api-docs': icon(<><path d="M3 3.4h6.4A1.6 1.6 0 0 1 11 5v8.6H4.6A1.6 1.6 0 0 1 3 12z" /><path d="M11 5h1.4a1.6 1.6 0 0 1 1.6 1.6v7H11" /></>),
  deliveries: icon(<><path d="M2.4 4h6.4v6H2.4zM8.8 6.6h2.4l2 2v1.4H8.8z" /><circle cx="5" cy="12" r="1.1" /><circle cx="11.4" cy="12" r="1.1" /></>),
  reports: icon(<><path d="M2.4 13.4h11.2" /><path d="M4.4 11V6.6M7.4 11V3.4M10.4 11V8.2M13 11V5" /></>),
  mail: icon(<><rect x="2" y="3.4" width="12" height="9.2" rx="1.4" /><path d="m2.6 4.6 5.4 4 5.4-4" /></>),
  tickets: icon(<><circle cx="8" cy="8" r="6" /><path d="M8 11.6v.01M6.2 6.4a1.8 1.8 0 1 1 2.6 1.7c-.5.3-.8.6-.8 1.3" /></>),
  activity: icon(<><path d="M1.8 8h3l1.6-4 2.4 8 1.6-4h3.8" /></>),
};
const DEFAULT_ICON = icon(<circle cx="8" cy="8" r="2.2" />);

export function Layout({
  nav,
  current,
  onNavigate,
  title,
  subtitle,
  actions,
  user,
  onLogout,
  children,
}: {
  nav: NavEntry[];
  current: string;
  onNavigate: (key: string) => void;
  title: string;
  subtitle: string;
  actions?: ReactNode;
  user: string;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const go = (key: string) => {
    setNavOpen(false);
    onNavigate(key);
  };
  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--wc-page)' }} className={`mshell${navOpen ? ' nav-open' : ''}`}>
      {navOpen ? <div className="mbackdrop" onClick={() => setNavOpen(false)} /> : null}
      <aside className="mside">
        <div className="mbrand">
          <Logo height={52} className="mlogo" />
          <span className="mlive">LIVE</span>
        </div>

        <nav aria-label="Sections" className="mnav">
          {nav.map((entry, i) =>
            'heading' in entry ? (
              <div key={`h${i}`} className="mnav-head">
                {entry.heading}
              </div>
            ) : (
              <button
                key={entry.key}
                type="button"
                onClick={() => go(entry.key)}
                aria-current={current === entry.key ? 'page' : undefined}
                className={`mnav-item${current === entry.key ? ' is-active' : ''}`}
              >
                <span className="mnav-ico">{NAV_ICONS[entry.key] ?? DEFAULT_ICON}</span>
                <span className="mnav-label">{entry.label}</span>
                {entry.badge ? <span className="mnav-badge">{entry.badge}</span> : null}
              </button>
            ),
          )}
        </nav>

        <div className="mside-foot">
          <div className="mside-user">
            <span className="mside-avatar" aria-hidden>
              {(user || 'W').trim().charAt(0).toUpperCase()}
            </span>
            <span className="mside-name" title={user}>
              {user}
            </span>
          </div>
          <button type="button" onClick={onLogout} className="mside-signout">
            Sign out
          </button>
        </div>
      </aside>

      <div className="mmain">
        <header className="mtop">
          <button type="button" className="mham" aria-label="Menu" onClick={() => setNavOpen((o) => !o)}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round">
              <path d="M3 5h12M3 9h12M3 13h12" />
            </svg>
          </button>
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <h1 style={{ fontSize: 20, letterSpacing: '-0.02em' }}>{title}</h1>
            {subtitle ? <p className="mtop-sub">{subtitle}</p> : null}
          </div>
          {actions ? <div className="mtop-actions">{actions}</div> : null}
        </header>
        <div className="mcontent">{children}</div>
      </div>

      <style>{`
        .mside {
          width: 238px; flex: none; background: var(--wc-surface);
          border-right: 1px solid var(--wc-border); display: flex; flex-direction: column;
          position: sticky; top: 0; height: 100vh;
        }
        .mbrand {
          height: 72px; flex: none; display: flex; align-items: center; gap: 9px;
          padding: 0 16px; border-bottom: 1px solid #EFF3F7;
        }
        .mlogo { max-width: 170px; object-fit: contain; }
        .mlive {
          margin-left: auto; font: 600 9px/1 var(--wc-font); letter-spacing: .07em;
          color: #0E7A5A; background: #E7F6EF; border: 1px solid #CDEBDE;
          padding: 3px 5px; border-radius: 4px;
        }
        .mnav { flex: 1; min-height: 0; overflow-y: auto; padding: 12px 10px 10px; }
        .mnav-head {
          font: 600 9.5px/1 var(--wc-font); letter-spacing: .1em; color: var(--wc-faint);
          padding: 16px 10px 6px;
        }
        .mnav-head:first-child { padding-top: 4px; }
        .mnav-item {
          display: flex; align-items: center; gap: 9px; width: 100%;
          padding: 7px 10px; margin: 1px 0; border: none; border-radius: 7px;
          background: transparent; color: var(--wc-body);
          font: 500 13px/1 var(--wc-font); text-align: left; cursor: pointer;
          transition: background .13s, color .13s;
        }
        .mnav-ico { display: inline-flex; opacity: .72; flex: none; }
        .mnav-label { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .mnav-item:hover { background: #EEF2F7; color: var(--wc-ink); }
        .mnav-item:hover .mnav-ico { opacity: .9; }
        .mnav-item.is-active { background: var(--wc-blue-header); color: #fff; }
        .mnav-item.is-active .mnav-ico { opacity: 1; }
        .mnav-badge {
          margin-left: auto; font: 600 10px/1 var(--wc-mono); color: var(--wc-blue);
          background: var(--wc-blue-tint-2); padding: 3px 5px; border-radius: 4px;
        }
        .mnav-item.is-active .mnav-badge { color: #fff; background: rgba(255,255,255,.16); }
        .mside-foot { flex: none; border-top: 1px solid #EFF3F7; padding: 10px 12px; }
        .mside-user { display: flex; align-items: center; gap: 9px; min-width: 0; }
        .mside-avatar {
          width: 28px; height: 28px; flex: none; border-radius: 8px; display: grid; place-items: center;
          background: var(--wc-blue-header); color: #fff; font: 600 12px/1 var(--wc-font);
        }
        .mside-name {
          flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font: 500 12.5px/1.2 var(--wc-font); color: var(--wc-ink);
        }
        .mside-signout {
          margin-top: 8px; width: 100%; padding: 7px 10px; border: 1px solid var(--wc-border);
          border-radius: 8px; background: var(--wc-surface); color: var(--wc-muted);
          font: 500 12.5px/1 var(--wc-font); cursor: pointer; transition: background .13s, color .13s;
        }
        .mside-signout:hover { background: var(--wc-surface-alt); color: var(--wc-ink); }
        .mmain { flex: 1; min-width: 0; display: flex; flex-direction: column; }
        .mtop {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
          padding: 18px 26px; border-bottom: 1px solid var(--wc-border); background: var(--wc-surface);
          position: sticky; top: 0; z-index: 5; flex-wrap: wrap;
        }
        .mtop-sub { margin: 3px 0 0; color: var(--wc-muted); font-size: 13.5px; }
        .mtop-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .mcontent { padding: 22px 26px 40px; }
        .mham { display: none; align-items: center; justify-content: center; flex: none;
          width: 34px; height: 34px; border: 1px solid var(--wc-border); border-radius: 8px;
          background: var(--wc-surface); color: var(--wc-ink); cursor: pointer; }
        .mbackdrop { display: none; }
        @media (max-width: 900px) {
          .mside {
            position: fixed; z-index: 40; transform: translateX(-100%); transition: transform .2s;
            box-shadow: var(--wc-shadow-lift);
          }
          .mshell.nav-open .mside { transform: none; }
          .mmain { width: 100%; }
          .mham { display: inline-flex; }
          .mbackdrop { display: block; position: fixed; inset: 0; z-index: 39; background: rgba(12,29,51,.34); }
        }
      `}</style>
    </div>
  );
}
